import { Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';
import { MdOutlinePayments, MdOutlineSavings } from 'react-icons/md';
import { FiChevronDown, FiDownload, FiFileText, FiCalendar, FiSearch } from 'react-icons/fi';
import AdvisorTableEmptyState from '../AdvisorTableEmptyState';
import { useClientDetail } from './ClientDetailContext';
import { Card, CardHeader, CardTitle, DataFooter, DataSummaryCard, DataSummaryGrid, DataSummaryIcon, DataSummaryValue, DataTable, DataTableScroll, DataToolbar, DetailLabel, FilterSelect, OrdersCreated, OrdersDateFilter, OrdersExport, OrdersLayout, OrdersSelectField, PageInfo, ReferenceText, SearchField, TransactionAmount, TransactionDescription, TransactionTypeBadge } from './styles';
import { FILTER_PLAN_TYPES, TRANSACTION_TYPES, downloadCsv, formatCompactDateTime, formatCount, formatCurrency, formatDateTime, formatPlanType, formatShortId, getTransactionsWithActivity, getValueTone, isWithinDays } from './helpers';
import { OrdersPropertyCode, PaginationControls } from './components';
import { useActivityLayout } from './hooks';

const TRANSACTIONS_PAGE_SIZE = 10;

const TransactionsLayout = styled(OrdersLayout)`
  ${DataTable} tbody tr {
    cursor: default;
  }
`;

const TransactionReferenceText = styled(ReferenceText)`
  max-width: 96px;
`;

const TransactionsTab = () => {
  const { detail, properties, propertiesLoading } = useClientDetail();
  const { containerRef, summaryLayout, filterLayout } = useActivityLayout();
  const [search, setSearch] = useState('');
  const [transactionType, setTransactionType] = useState('all');
  const [planType, setPlanType] = useState('all');
  const [days, setDays] = useState('all');
  const [page, setPage] = useState(1);
  const normalizedSearch = search.trim().toLowerCase();
  const filteredTransactions = getTransactionsWithActivity(detail.transactions)
    .filter((transaction) =>
      [
        transaction.id,
        transaction.description,
        transaction.externalId,
        transaction.planType,
        transaction.activity.label,
        transaction.activity.propertyCode,
      ].some((value) =>
        String(value || '')
          .toLowerCase()
          .includes(normalizedSearch),
      ),
    )
    .filter(
      (transaction) =>
        transactionType === 'all' ||
        transaction.activity.label.toLowerCase() === transactionType,
    )
    .filter(
      (transaction) => planType === 'all' || transaction.planType === planType,
    )
    .filter(
      (transaction) => days === 'all' || isWithinDays(transaction.date, days),
    );
  const totalPages = Math.max(
    1,
    Math.ceil(filteredTransactions.length / TRANSACTIONS_PAGE_SIZE),
  );
  const currentPage = Math.min(page, totalPages);
  const firstIndex = (currentPage - 1) * TRANSACTIONS_PAGE_SIZE;
  const visibleTransactions = filteredTransactions.slice(
    firstIndex,
    firstIndex + TRANSACTIONS_PAGE_SIZE,
  );
  const totalDeposits = filteredTransactions.reduce(
    (total, transaction) =>
      transaction.activity.label === 'Deposit'
        ? total + Number(transaction.amount)
        : total,
    0,
  );
  const totalWithdrawals = filteredTransactions.reduce(
    (total, transaction) =>
      transaction.activity.label === 'Withdrawal'
        ? total + Math.abs(Number(transaction.amount))
        : total,
    0,
  );
  useEffect(() => setPage(1), [search, transactionType, planType, days]);

  const exportTransactions = () => {
    downloadCsv('client-transactions.csv', [
      [
        'Reference',
        'Type',
        'Investment',
        'Description',
        'Amount',
        'Plan',
        'Date',
      ],
      ...filteredTransactions.map((transaction) => [
        transaction.externalId,
        transaction.activity.label,
        transaction.activity.propertyCode,
        transaction.description,
        transaction.amount,
        formatPlanType(transaction.planType),
        transaction.date,
      ]),
    ]);
  };

  const summary = [
    {
      Icon: FiFileText,
      label: 'Transactions',
      value: formatCount(filteredTransactions.length),
    },
    {
      Icon: MdOutlineSavings,
      label: 'Deposits',
      value: formatCurrency(totalDeposits),
      tone: 'positive',
    },
    {
      Icon: MdOutlinePayments,
      label: 'Withdrawals',
      value: formatCurrency(totalWithdrawals),
      tone: 'negative',
    },
  ];

  return (
    <TransactionsLayout
      ref={containerRef}
      data-summary-layout={summaryLayout}
      data-filter-layout={filterLayout}
    >
      <Card>
        <CardHeader>
          <CardTitle>Transactions</CardTitle>
          <OrdersExport
            type="button"
            disabled={!filteredTransactions.length}
            onClick={exportTransactions}
          >
            <FiDownload aria-hidden="true" />
            Export
          </OrdersExport>
        </CardHeader>
        <DataSummaryGrid>
          {summary.map(({ Icon, label, value, tone }) => (
            <DataSummaryCard key={label}>
              <DataSummaryIcon>
                <Icon aria-hidden="true" />
              </DataSummaryIcon>
              <div>
                <DetailLabel>{label}</DetailLabel>
                <DataSummaryValue className={tone}>{value}</DataSummaryValue>
              </div>
            </DataSummaryCard>
          ))}
        </DataSummaryGrid>
        <DataToolbar>
          <SearchField>
            <FiSearch aria-hidden="true" />
            <input
              type="search"
              value={search}
              placeholder="Search transactions..."
              aria-label="Search client transactions"
              onChange={(event) => setSearch(event.target.value)}
            />
          </SearchField>
          <OrdersSelectField>
            <FilterSelect
              value={planType}
              aria-label="Filter transactions by plan"
              onChange={(event) => setPlanType(event.target.value)}
            >
              <option value="all">All plans</option>
              {FILTER_PLAN_TYPES.map((type) => (
                <option key={type} value={type}>
                  {formatPlanType(type)}
                </option>
              ))}
            </FilterSelect>
            <FiChevronDown aria-hidden="true" />
          </OrdersSelectField>
          <OrdersSelectField>
            <FilterSelect
              value={transactionType}
              aria-label="Filter transactions by type"
              onChange={(event) => setTransactionType(event.target.value)}
            >
              <option value="all">All types</option>
              {Object.keys(TRANSACTION_TYPES).map((type) => (
                <option key={type} value={type.toLowerCase()}>
                  {type}
                </option>
              ))}
            </FilterSelect>
            <FiChevronDown aria-hidden="true" />
          </OrdersSelectField>
          <OrdersDateFilter>
            <FiCalendar aria-hidden="true" />
            <FilterSelect
              value={days}
              aria-label="Filter transactions by date"
              onChange={(event) => setDays(event.target.value)}
            >
              <option value="all">All time</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last 12 months</option>
            </FilterSelect>
            <FiChevronDown aria-hidden="true" />
          </OrdersDateFilter>
        </DataToolbar>
        {visibleTransactions.length ? (
          <Fragment>
            <DataTableScroll>
              <DataTable>
                <thead>
                  <tr>
                    <th>Reference</th>
                    <th>Type</th>
                    <th>Investment</th>
                    <th>Description</th>
                    <th className="numeric">Amount</th>
                    <th>Plan</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleTransactions.map((transaction, index) => (
                    <tr key={transaction.id || index}>
                      <td>
                        {transaction.externalId ? (
                          <TransactionReferenceText
                            title={transaction.externalId}
                          >
                            {formatShortId(transaction.externalId)}
                          </TransactionReferenceText>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        <TransactionTypeBadge
                          className={
                            TRANSACTION_TYPES[transaction.activity.label]
                          }
                        >
                          {transaction.activity.label}
                        </TransactionTypeBadge>
                      </td>
                      <td>
                        {transaction.activity.propertyCode ? (
                          <OrdersPropertyCode
                            order={transaction.activity}
                            properties={properties}
                            propertiesLoading={propertiesLoading}
                          />
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        <TransactionDescription
                          title={transaction.description || ''}
                        >
                          {transaction.description || '—'}
                        </TransactionDescription>
                      </td>
                      <td className="numeric">
                        <TransactionAmount
                          className={getValueTone(transaction.amount)}
                        >
                          {formatCurrency(transaction.amount)}
                        </TransactionAmount>
                      </td>
                      <td>{formatPlanType(transaction.planType)}</td>
                      <td>
                        <OrdersCreated title={formatDateTime(transaction.date)}>
                          {formatCompactDateTime(transaction.date)}
                        </OrdersCreated>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </DataTableScroll>
            <DataFooter>
              <PageInfo>
                {`Showing ${firstIndex + 1} to ${Math.min(
                  firstIndex + TRANSACTIONS_PAGE_SIZE,
                  filteredTransactions.length,
                )} of ${filteredTransactions.length} transactions`}
              </PageInfo>
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                label="Transaction pages"
                onChange={setPage}
              />
            </DataFooter>
          </Fragment>
        ) : (
          <DataTableScroll>
            <AdvisorTableEmptyState>
              {detail.transactions.length === 0
                ? 'No transactions.'
                : 'No transactions match the selected filters.'}
            </AdvisorTableEmptyState>
          </DataTableScroll>
        )}
      </Card>
    </TransactionsLayout>
  );
};

export default TransactionsTab;
